import type { ReactNode } from 'react';
import { asLines } from './Lines';

/**
 * 구획과 카드 (19회차, 콘솔 검토 A-4(a)·(f)).
 *
 * 16회차까지 섹션 카드 클래스가 17곳, 구획 머리가 화면마다 제각각이었다.
 * 배치와 밀도는 18회차까지의 화면을 따른다(19회차 보완, 2026-09-08 사용자 피드백 1) —
 * 구획 제목 18px, 구획 사이 24px, 카드 안 여백 16~20px, 둥글기 12px.
 *
 * 구획은 두 가지 모양이다.
 *   - 기본: 머리(제목·설명·오른쪽 배지)가 바깥에 있고 본문이 카드·표로 온다. 실시간 화면
 *   - `boxed`: 머리까지 카드 하나에 들어간다. 검색·알림·저장소처럼 조건과 표가 한 덩어리인 화면
 */

/** 화면 하나. 구획 사이는 24px */
export function Page({ children }: { children: ReactNode }) {
  return <div className="space-y-6">{children}</div>;
}

/** 흰 카드. 테두리는 장식 실선이고, 안쪽 여백은 16px */
export function Card({
  children,
  tone,
  padded = true,
}: {
  children: ReactNode;
  /** 오류 카드는 글자만 빨강으로 */
  tone?: 'error' | 'muted';
  padded?: boolean;
}) {
  const text = tone === 'error' ? 'text-error' : tone === 'muted' ? 'text-muted' : 'text-ink';
  return (
    <div
      className={`rounded-xl border border-hair bg-panel ${padded ? 'p-4' : ''} text-sm ${text}`}
    >
      {children}
    </div>
  );
}

/**
 * 구획 하나. 머리(제목·설명·오른쪽 값)와 본문으로 나뉜다.
 *
 * `compact`는 저장소의 종류별 표처럼 작은 제목 하나와 오른쪽 한 줄 요약만 있는 구획이다.
 * 그때 `note`는 제목 아래가 아니라 오른쪽에 붙는다.
 */
export function Section({
  title,
  note,
  badge,
  actions,
  children,
  titleId,
  boxed = false,
  compact = false,
}: {
  title: string;
  note?: ReactNode;
  /** 오른쪽 위에 붙는 숫자 배지 */
  badge?: string;
  /** 오른쪽 위 단추들 */
  actions?: ReactNode;
  children: ReactNode;
  titleId?: string;
  /** 머리까지 카드 하나에 넣는다 */
  boxed?: boolean;
  /** 작은 제목과 오른쪽 한 줄 요약 */
  compact?: boolean;
}) {
  const head = compact ? (
    <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
      <h2 id={titleId} className="text-sm font-semibold text-ink">
        {title}
      </h2>
      <div className="flex flex-wrap items-center gap-3">
        {note ? (
          <p className="tabular whitespace-pre-line break-words text-xs text-muted">
            {asLines(note)}
          </p>
        ) : null}
        {badge ? <Badge>{badge}</Badge> : null}
        {actions}
      </div>
    </div>
  ) : (
    <div className={`${boxed ? 'mb-4' : 'mb-3'} flex flex-wrap items-end justify-between gap-3`}>
      <div className="min-w-0">
        <h2 id={titleId} className="break-words text-lg font-semibold text-ink">
          {title}
        </h2>
        {note ? (
          <p className="mt-1 max-w-3xl whitespace-pre-line break-words text-sm text-muted">
            {asLines(note)}
          </p>
        ) : null}
      </div>
      {badge || actions ? (
        <div className="flex flex-wrap items-center gap-2">
          {badge ? <Badge>{badge}</Badge> : null}
          {actions}
        </div>
      ) : null}
    </div>
  );

  return (
    <section
      aria-labelledby={titleId}
      className={boxed ? 'rounded-xl border border-hair bg-band p-5' : undefined}
    >
      {head}
      {children}
    </section>
  );
}

/** 구획 머리 오른쪽의 작은 숫자 배지 */
function Badge({ children }: { children: ReactNode }) {
  return (
    <span className="tabular rounded-full bg-band-strong px-3 py-1 text-xs text-muted">
      {children}
    </span>
  );
}

/** 카드 안에서 한 단계 더 나누는 작은 제목 */
export function Subsection({
  title,
  note,
  actions,
  children,
}: {
  title: string;
  note?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-ink">{title}</h3>
          {note ? (
            <p className="mt-0.5 whitespace-pre-line break-words text-xs text-muted">
              {asLines(note)}
            </p>
          ) : null}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}
